import {
  automationQueries,
  GeneralConfig,
  GeneralConfigUpdate,
  updateGeneralConfig,
} from "@/api/automations.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { userCreditQueries } from "@/api/user-credits.ts";
import { toast } from "@/hooks/use-toast.ts";
import { CalendarDays, Clock, DollarSign, Globe2, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge.tsx";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button.tsx";
import { H6 } from "@/components/ui/h6.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import TimezoneSelect from "react-timezone-select";
import { TIMEZONES } from "@/components/TimeAndTzPicker.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Switch } from "@/components/ui/switch.tsx";
import { DialogFooter } from "@/components/ui/dialog.tsx";
import { LoadingButton } from "@/components/LoadingButton.tsx";

interface GeneralConfigCardProps {
  config: GeneralConfig;
  agentId: string;
}

export function EditGeneralConfig({ config, agentId }: GeneralConfigCardProps) {
  const queryClient = useQueryClient();
  const [editedConfig, setEditedConfig] = useState<GeneralConfig>(config);

  const { data: creditSummary } = useQuery(userCreditQueries.summary(agentId));

  // Use 'updateGeneralConfig' for partial updates
  const mutation = useMutation({
    mutationFn: (data: GeneralConfigUpdate) =>
      updateGeneralConfig(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(automationQueries.generalConfig(agentId));
      queryClient.invalidateQueries(automationQueries.automations(agentId));
      toast({
        title: "Settings Updated",
        description: "Your automation settings have been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSave = async () => {
    // Construct partial updates
    const updates: GeneralConfigUpdate = {
      operation_window_start: editedConfig.operation_window_start,
      operation_window_end: editedConfig.operation_window_end,
      max_posts_per_day: editedConfig.max_posts_per_day,
      auto_replenish_credits: editedConfig.auto_replenish_credits,
      auto_replenish_threshold: editedConfig.auto_replenish_threshold,
      auto_replenish_amount: editedConfig.auto_replenish_amount,
      timezone: editedConfig.timezone,
      // no need to pass 'metadata' if not changed
    };

    mutation.mutate(updates);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {/* 1) Operation Window */}
          <div className="bg-background/50 rounded-lg p-3 flex flex-col items-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Clock className="h-4 w-4" />
              Operation Window
            </div>
            <div className="text-lg font-semibold text-center">
              {config.operation_window_start.toString().padStart(2, "0")}:00 -{" "}
              {config.operation_window_end.toString().padStart(2, "0")}:00
              <div className="text-xs text-muted-foreground flex items-center justify-center gap-1 mt-1">
                <Globe2 className="h-3 w-3" />
                {config.timezone}
              </div>
            </div>
          </div>

          {/* 2) Daily Limit */}
          <div className="bg-background/50 rounded-lg p-3 flex flex-col items-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <CalendarDays className="h-4 w-4" />
              Daily Limit
            </div>
            <div className="text-lg font-semibold text-center">
              {config.max_posts_per_day}
              <span className="text-sm font-normal text-muted-foreground ml-1">
                posts per day
              </span>
            </div>
          </div>

          {/* 3) Credit Info */}
          <div className="bg-background/50 rounded-lg p-3 flex flex-col items-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Zap className="h-4 w-4" />
              Credit Management
            </div>
            <div className="text-lg font-semibold text-center">
              {creditSummary?.available_credits ?? 0}
              <span className="text-sm font-normal text-muted-foreground ml-1">
                credits
              </span>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2 w-full">
              <div>
                {config.auto_replenish_credits ? (
                  <Badge className="bg-primary/20">Auto-Replenish</Badge>
                ) : (
                  <Badge variant="secondary">Manual</Badge>
                )}
              </div>
              <Link to="/account?tab=credits">
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <DollarSign className="h-3 w-3 mr-1" />
                  Get Credits
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Operation Window */}
        <div className="space-y-4">
          <H6>Operating Hours</H6>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Start Time</Label>
              <Select
                value={editedConfig.operation_window_start.toString()}
                onValueChange={(value) =>
                  setEditedConfig((prev) => ({
                    ...prev,
                    operation_window_start: parseInt(value),
                  }))
                }
                disabled={mutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <SelectItem key={hour} value={hour.toString()}>
                      {hour.toString().padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">End Time</Label>
              <Select
                value={editedConfig.operation_window_end.toString()}
                onValueChange={(value) =>
                  setEditedConfig((prev) => ({
                    ...prev,
                    operation_window_end: parseInt(value),
                  }))
                }
                disabled={mutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <SelectItem key={hour} value={hour.toString()}>
                      {hour.toString().padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 space-y-2">
              <Label className="text-sm">Timezone</Label>
              <TimezoneSelect
                value={editedConfig.timezone}
                onChange={(e) =>
                  setEditedConfig((prev) => ({
                    ...prev,
                    timezone: e.value,
                  }))
                }
                isDisabled={mutation.isPending}
                timezones={TIMEZONES}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isDisabled
                      ? "hsl(var(--muted-background))"
                      : "hsl(var(--background))",
                    borderColor: "hsl(var(--border))",
                    borderStyle: "solid",
                    borderRadius: "var(--radius)",
                    height: "40px",
                    opacity: state.isDisabled ? 0.5 : 1,
                    cursor: state.isDisabled ? "not-allowed" : "pointer",
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    backgroundColor: "hsl(var(--background))",
                    borderColor: "hsl(var(--border))",
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isFocused
                      ? "hsl(var(--accent))"
                      : "hsl(var(--background))",
                    color: state.isFocused
                      ? "hsl(var(--accent-foreground))"
                      : "hsl(var(--foreground))",
                    cursor: "pointer",
                  }),
                  singleValue: (baseStyles, state) => ({
                    ...baseStyles,
                    color: state.isDisabled
                      ? "hsl(var(--muted-foreground))"
                      : "hsl(var(--foreground))",
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                    color: "hsl(var(--foreground))",
                  }),
                }}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <H6>Posts Limit</H6>

          <div className="space-y-2">
            <Label className="text-sm">Max Posts per Day</Label>
            <Input
              type="number"
              min={1}
              value={editedConfig.max_posts_per_day}
              disabled={mutation.isPending}
              onChange={(e) =>
                setEditedConfig((prev) => ({
                  ...prev,
                  max_posts_per_day: parseInt(e.target.value),
                }))
              }
            />
          </div>
        </div>

        {/* Credit Management */}
        <div className="space-y-4">
          <Label>Credit Management</Label>

          <div className="flex items-center justify-between space-x-2">
            <Label className="text-muted-foreground">
              Automatically add credits when balance is low
            </Label>
            <Switch
              checked={editedConfig.auto_replenish_credits}
              disabled={mutation.isPending}
              onCheckedChange={(checked) =>
                setEditedConfig((prev) => ({
                  ...prev,
                  auto_replenish_credits: checked,
                }))
              }
            />
          </div>

          {editedConfig.auto_replenish_credits && (
            <div className="grid grid-cols-2 gap-4">
              {/* Replenish Threshold */}
              <div className="space-y-2">
                <Label className="text-sm">Replenish Threshold</Label>
                <Input
                  type="number"
                  min={1}
                  value={editedConfig.auto_replenish_threshold}
                  disabled={mutation.isPending}
                  onChange={(e) =>
                    setEditedConfig((prev) => ({
                      ...prev,
                      auto_replenish_threshold: parseFloat(e.target.value),
                    }))
                  }
                />
              </div>
              {/* Replenish Amount */}
              <div className="space-y-2">
                <Label className="text-sm">Replenish Amount</Label>
                <Input
                  type="number"
                  min={1}
                  disabled={mutation.isPending}
                  value={editedConfig.auto_replenish_amount}
                  onChange={(e) =>
                    setEditedConfig((prev) => ({
                      ...prev,
                      auto_replenish_amount: parseFloat(e.target.value),
                    }))
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <LoadingButton
          loading={mutation.isPending}
          onClick={() => handleSave()}
        >
          Save Settings
        </LoadingButton>
      </DialogFooter>
    </>
  );
}
